import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { confirmAsync } from '@/lib/confirm';
import { 
  ArrowLeft, Users, UserPlus, Phone, Mail, MessageSquare,
  BarChart3, Edit, Trash2, Upload,
  ChevronDown, ChevronUp, Package, Plus, Star, AlertTriangle,
  ClipboardList, Video, MapPin, FileText, Send, Copy
} from 'lucide-react';

const CONTACT_STATUSES = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-gray-400' },
  { value: 'email_enviado', label: 'Email enviado', color: 'bg-blue-400' },
  { value: 'whatsapp_enviado', label: 'WhatsApp enviado', color: 'bg-green-400' },
  { value: 'llamada_realizada', label: 'Llamada realizada', color: 'bg-yellow-500' },
  { value: 'interesado', label: 'Interesado', color: 'bg-purple-500' },
  { value: 'estudio_pendiente', label: 'Estudio pendiente', color: 'bg-indigo-500' },
  { value: 'en_negociacion', label: 'En negociacion', color: 'bg-orange-500' },
  { value: 'cerrado_ganado', label: 'Cerrado Ganado', color: 'bg-green-600' },
  { value: 'cerrado_perdido', label: 'Cerrado Perdido', color: 'bg-red-500' },
  { value: 'no_contesta', label: 'No contesta', color: 'bg-gray-500' },
];

const MODULE_STATUSES = [
  { value: 'no_presentado', label: 'No presentado' },
  { value: 'presentado', label: 'Presentado' },
  { value: 'interesado', label: 'Interesado' },
  { value: 'no_interesado', label: 'No interesado' },
  { value: 'pendiente_demo', label: 'Pendiente demo' },
  { value: 'presupuesto_enviado', label: 'Presupuesto enviado' },
  { value: 'cerrado', label: 'Cerrado' },
];

const INTERACTION_TYPES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'llamada', label: 'Llamada', icon: Phone },
  { value: 'reunion', label: 'Reunion', icon: Users },
  { value: 'demo', label: 'Demo', icon: Video },
  { value: 'visita', label: 'Visita', icon: MapPin },
];

const getStatusInfo = (status) => CONTACT_STATUSES.find(s => s.value === status) || CONTACT_STATUSES[0];

// ====== SUB-COMPONENTS ======

function ContactRow({ contact, onEdit, onModules, expandedContact, setExpandedContact, campaignId, onRefresh, campaign }) {
  const statusInfo = getStatusInfo(contact.estado);
  const isExpanded = expandedContact === contact.id;
  const [interactions, setInteractions] = useState([]);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ tipo: 'llamada', resultado: '', notas: '', duracion_minutos: '' });
  const [showSurveyForm, setShowSurveyForm] = useState(false);
  const [survey, setSurvey] = useState(null);

  const fetchInteractions = async () => {
    try {
      const res = await axiosInstance.get(`/campaigns/${campaignId}/contacts/${contact.id}/interactions`);
      setInteractions(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchSurvey = async () => {
    try {
      const res = await axiosInstance.get(`/campaigns/${campaignId}/contacts/${contact.id}/survey`);
      setSurvey(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchInteractions();
      fetchSurvey();
    }
  }, [isExpanded]);

  const handleAddInteraction = async () => {
    try {
      await axiosInstance.post(`/campaigns/${campaignId}/contacts/${contact.id}/interactions`, {
        ...interactionForm,
        duracion_minutos: interactionForm.duracion_minutos ? parseInt(interactionForm.duracion_minutos) : null
      });
      toast.success('Interaccion registrada');
      setShowInteractionForm(false);
      setInteractionForm({ tipo: 'llamada', resultado: '', notas: '', duracion_minutos: '' });
      fetchInteractions();
      onRefresh();
    } catch (e) {
      toast.error('Error al registrar interaccion');
    }
  };

  const handleDeleteInteraction = async (interactionId) => {
    try {
      await axiosInstance.delete(`/campaigns/${campaignId}/contacts/${contact.id}/interactions/${interactionId}`);
      toast.success('Interaccion eliminada');
      fetchInteractions();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const getInteractionIcon = (tipo) => {
    const t = INTERACTION_TYPES.find(i => i.value === tipo);
    return t ? t.icon : Phone;
  };

  return (
    <Card key={contact.id} className="hover:shadow-md transition-shadow" data-testid={`contact-row-${contact.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Badge className={`${statusInfo.color} text-white text-xs`} data-testid={`contact-status-${contact.id}`}>
            {statusInfo.label}
          </Badge>
          {contact.email_marketing_opt_out && (
            <Badge className="bg-red-100 text-red-700 text-xs border border-red-200" data-testid={`opt-out-badge-${contact.id}`}>
              Opt-out
            </Badge>
          )}
          {contact.client_status && contact.client_status !== 'active' && (
            <Badge className="bg-yellow-100 text-yellow-700 text-xs border border-yellow-200">
              {contact.client_status === 'cancelled' ? 'Cancelado' : contact.client_status === 'pending_cancel' ? 'Baja pend.' : 'Inactivo'}
            </Badge>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{contact.cliente_nombre}</p>
            <p className="text-sm text-gray-500 truncate">{contact.cliente_email}</p>
          </div>
          <div className="text-sm text-gray-500 hidden md:block">
            {contact.comercial_nombre || 'Sin asignar'}
          </div>
          {contact.probabilidad_cierre > 0 && (
            <div className="text-sm font-medium text-blue-600 hidden md:block">
              {contact.probabilidad_cierre}%
            </div>
          )}
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onModules(contact)} title="Ver modulos" data-testid={`modules-btn-${contact.id}`}>
              <Package className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onEdit(contact)} data-testid={`edit-btn-${contact.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExpandedContact(isExpanded ? null : contact.id)} data-testid={`expand-btn-${contact.id}`}>
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Basic details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Proxima accion</p>
                <p className="font-medium">
                  {contact.proxima_accion ? new Date(contact.proxima_accion).toLocaleDateString('es-ES') : '-'}
                </p>
                {contact.proxima_accion_descripcion && <p className="text-gray-500 text-xs">{contact.proxima_accion_descripcion}</p>}
              </div>
              <div>
                <p className="text-gray-500">Volumen estimado</p>
                <p className="font-medium">{contact.volumen_estimado?.toLocaleString('es-ES') || 0} EUR</p>
              </div>
              <div>
                <p className="text-gray-500">Estudio</p>
                <p className="font-medium">
                  {contact.estudio_enviado ? 'Enviado' : contact.estudio_solicitado ? 'Solicitado' : '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Notas</p>
                <p className="text-xs">{contact.notas || '-'}</p>
              </div>
            </div>

            {/* Survey badge */}
            {survey && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">Satisfaccion: {survey.nota_general}/10</span>
                <Badge className={survey.es_promotor ? 'bg-green-500 text-white' : survey.es_detractor ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}>
                  {survey.es_promotor ? 'Promotor' : survey.es_detractor ? 'Detractor' : 'Neutro'}
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setShowSurveyForm(true)} data-testid={`edit-survey-btn-${contact.id}`}>
                  Editar encuesta
                </Button>
              </div>
            )}
            {!survey && (
              <Button variant="outline" size="sm" onClick={() => setShowSurveyForm(true)} data-testid={`add-survey-btn-${contact.id}`}>
                <Star className="w-4 h-4 mr-2" /> Rellenar encuesta satisfaccion
              </Button>
            )}

            {/* Quick send Email/WhatsApp from template */}
            {contact.email_marketing_opt_out && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Este cliente ha solicitado no recibir comunicaciones comerciales (opt-out). No se pueden enviar plantillas.</span>
              </div>
            )}
            {!contact.email_marketing_opt_out && (campaign?.plantilla_email_cuerpo || campaign?.plantilla_whatsapp) && (
              <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-blue-700 mr-2">Enviar plantilla:</span>
                {campaign?.plantilla_email_cuerpo && contact.cliente_email && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid={`send-email-template-${contact.id}`}
                    onClick={async () => {
                      try {
                        const res = await axiosInstance.post(`/marketing/send-single-email`, {
                          campaign_id: campaignId,
                          contact_id: contact.id,
                          client_id: contact.cliente_id
                        });
                        toast.success(`Email enviado a ${contact.cliente_email}`);
                        fetchInteractions();
                        onRefresh();
                      } catch (e) {
                        const msg = e.response?.data?.detail || 'Error al enviar email';
                        toast.error(msg);
                      }
                    }}
                  >
                    <Mail className="w-4 h-4 mr-1" /> Enviar Email
                  </Button>
                )}
                {campaign?.plantilla_whatsapp && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-100"
                    data-testid={`send-whatsapp-template-${contact.id}`}
                    onClick={() => {
                      const msg = (campaign.plantilla_whatsapp || '').replace(/{nombre}/g, contact.cliente_nombre || '');
                      // Try to extract phone from email or use generic WhatsApp web
                      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`);
                      // Auto-register interaction
                      axiosInstance.post(`/campaigns/${campaignId}/contacts/${contact.id}/interactions`, {
                        tipo: 'whatsapp', resultado: 'WhatsApp plantilla enviado', notas: msg.substring(0, 200)
                      }).then(() => { toast.success('WhatsApp abierto e interaccion registrada'); fetchInteractions(); onRefresh(); });
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" /> Enviar WhatsApp
                  </Button>
                )}
              </div>
            )}

            {/* Interactions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm text-gray-700">Interacciones ({interactions.length})</h4>
                <Button variant="outline" size="sm" onClick={() => setShowInteractionForm(!showInteractionForm)} data-testid={`add-interaction-btn-${contact.id}`}>
                  <Plus className="w-4 h-4 mr-1" /> Registrar
                </Button>
              </div>

              {showInteractionForm && (
                <div className="p-3 border rounded-lg mb-3 space-y-3 bg-gray-50" data-testid={`interaction-form-${contact.id}`}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select value={interactionForm.tipo} onValueChange={v => setInteractionForm({...interactionForm, tipo: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {INTERACTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Resultado</Label>
                      <Input placeholder="Ej: Interesado en KDS" value={interactionForm.resultado} onChange={e => setInteractionForm({...interactionForm, resultado: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-xs">Duracion (min)</Label>
                      <Input type="number" placeholder="15" value={interactionForm.duracion_minutos} onChange={e => setInteractionForm({...interactionForm, duracion_minutos: e.target.value})} />
                    </div>
                    <div className="flex items-end">
                      <Button size="sm" onClick={handleAddInteraction} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid={`save-interaction-btn-${contact.id}`}>Guardar</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Notas</Label>
                    <Textarea rows={2} placeholder="Notas de la interaccion..." value={interactionForm.notas} onChange={e => setInteractionForm({...interactionForm, notas: e.target.value})} />
                  </div>
                </div>
              )}

              {interactions.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {interactions.map(interaction => {
                    const Icon = getInteractionIcon(interaction.tipo);
                    return (
                      <div key={interaction.id} className="flex items-start gap-3 p-2 border rounded text-sm" data-testid={`interaction-${interaction.id}`}>
                        <Icon className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{interaction.tipo}</span>
                            {interaction.duracion_minutos && <span className="text-gray-400 text-xs">{interaction.duracion_minutos} min</span>}
                            <span className="text-gray-400 text-xs">{new Date(interaction.fecha).toLocaleDateString('es-ES')}</span>
                          </div>
                          {interaction.resultado && <p className="text-gray-600">{interaction.resultado}</p>}
                          {interaction.notas && <p className="text-gray-400 text-xs">{interaction.notas}</p>}
                          <p className="text-gray-400 text-xs">Por: {interaction.usuario_comercial_nombre || '-'}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteInteraction(interaction.id)} className="shrink-0">
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Survey Form Dialog */}
            {showSurveyForm && (
              <SurveyForm
                campaignId={campaignId}
                contactId={contact.id}
                existing={survey}
                onClose={() => setShowSurveyForm(false)}
                onSaved={() => { fetchSurvey(); onRefresh(); setShowSurveyForm(false); }}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SurveyForm({ campaignId, contactId, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    nota_general: existing?.nota_general || 7,
    usa_tpv: existing?.usa_tpv || false,
    usa_comandero: existing?.usa_comandero || false,
    usa_kds: existing?.usa_kds || false,
    usa_pedidos_online: existing?.usa_pedidos_online || false,
    usa_reservas: existing?.usa_reservas || false,
    usa_fidelizacion: existing?.usa_fidelizacion || false,
    comentario_mejoras: existing?.comentario_mejoras || '',
    problemas_detectados: existing?.problemas_detectados || '',
    funcionalidad_solicitada: existing?.funcionalidad_solicitada || '',
    que_le_gusta: existing?.que_le_gusta || '',
  });

  const handleSave = async () => {
    try {
      await axiosInstance.post(`/campaigns/${campaignId}/contacts/${contactId}/survey`, form);
      toast.success('Encuesta guardada');
      onSaved();
    } catch (e) {
      toast.error('Error al guardar encuesta');
    }
  };

  const moduleChecks = [
    { key: 'usa_tpv', label: 'TPV' },
    { key: 'usa_comandero', label: 'Comandero' },
    { key: 'usa_kds', label: 'KDS' },
    { key: 'usa_pedidos_online', label: 'Pedidos Online' },
    { key: 'usa_reservas', label: 'Reservas' },
    { key: 'usa_fidelizacion', label: 'Fidelizacion' },
  ];

  return (
    <div className="p-4 border rounded-lg bg-yellow-50 space-y-4" data-testid={`survey-form-${contactId}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-medium flex items-center gap-2"><Star className="w-4 h-4 text-yellow-500" /> Encuesta de Satisfaccion</h4>
        <Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>
      </div>

      <div>
        <Label>Nota general (1-10)</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            type="range" min="1" max="10" value={form.nota_general}
            onChange={e => setForm({...form, nota_general: parseInt(e.target.value)})}
            className="flex-1"
          />
          <span className={`text-2xl font-bold ${form.nota_general >= 9 ? 'text-green-600' : form.nota_general <= 6 ? 'text-red-600' : 'text-yellow-600'}`}>
            {form.nota_general}
          </span>
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Modulos que utiliza</Label>
        <div className="flex flex-wrap gap-3">
          {moduleChecks.map(m => (
            <label key={m.key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form[m.key]} onChange={e => setForm({...form, [m.key]: e.target.checked})} className="rounded" />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Que le gusta</Label>
          <Textarea rows={2} value={form.que_le_gusta} onChange={e => setForm({...form, que_le_gusta: e.target.value})} placeholder="Lo que mas valora..." />
        </div>
        <div>
          <Label className="text-xs">Mejoras sugeridas</Label>
          <Textarea rows={2} value={form.comentario_mejoras} onChange={e => setForm({...form, comentario_mejoras: e.target.value})} placeholder="Que mejoraria..." />
        </div>
        <div>
          <Label className="text-xs">Problemas detectados</Label>
          <Textarea rows={2} value={form.problemas_detectados} onChange={e => setForm({...form, problemas_detectados: e.target.value})} placeholder="Incidencias o problemas..." />
        </div>
        <div>
          <Label className="text-xs">Funcionalidad solicitada</Label>
          <Textarea rows={2} value={form.funcionalidad_solicitada} onChange={e => setForm({...form, funcionalidad_solicitada: e.target.value})} placeholder="Nueva funcionalidad..." />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid={`save-survey-btn-${contactId}`}>Guardar Encuesta</Button>
      </div>
    </div>
  );
}

function AlertsPanel({ campaignId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await axiosInstance.get('/campaigns/alerts/all');
      // Filter to current campaign if provided
      const filtered = campaignId ? res.data.filter(a => a.campaign_id === campaignId) : res.data;
      setAlerts(filtered);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const markRead = async (alertId) => {
    try {
      await axiosInstance.put(`/campaigns/alerts/${alertId}/read`);
      fetchAlerts();
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Cargando alertas...</div>;

  return (
    <div className="space-y-3" data-testid="alerts-panel">
      {alerts.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-gray-500">No hay alertas</CardContent></Card>
      ) : (
        alerts.map(alert => (
          <Card key={alert.id} className={`border-l-4 ${alert.prioridad === 'alta' ? 'border-l-red-500' : 'border-l-yellow-400'} ${alert.leida ? 'opacity-60' : ''}`} data-testid={`alert-${alert.id}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 shrink-0 ${alert.prioridad === 'alta' ? 'text-red-500' : 'text-yellow-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{alert.titulo}</p>
                <p className="text-sm text-gray-500">{alert.mensaje}</p>
                <p className="text-xs text-gray-400 mt-1">{new Date(alert.created_at).toLocaleDateString('es-ES')}</p>
              </div>
              {!alert.leida && (
                <Button variant="ghost" size="sm" onClick={() => markRead(alert.id)} data-testid={`mark-read-${alert.id}`}>
                  Marcar leida
                </Button>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function MyTasksPanel() {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axiosInstance.get('/campaigns/comercial/tasks');
      setTasks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Cargando tareas...</div>;
  if (!tasks) return null;

  const renderTaskList = (list, title, color) => (
    list.length > 0 && (
      <div>
        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${color}`}></span> {title} ({list.length})
        </h4>
        <div className="space-y-2">
          {list.map(task => (
            <Card key={task.id} data-testid={`task-${task.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="font-medium text-sm">{task.cliente_nombre}</p>
                  <p className="text-xs text-gray-500">{task.campaign_nombre}</p>
                  {task.proxima_accion_descripcion && <p className="text-xs text-gray-400">{task.proxima_accion_descripcion}</p>}
                </div>
                <Badge className="bg-gray-200 text-gray-700 text-xs">{task.estado}</Badge>
                {task.proxima_accion && (
                  <span className="text-xs text-gray-400">{new Date(task.proxima_accion).toLocaleDateString('es-ES')}</span>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  );

  return (
    <div className="space-y-6" data-testid="my-tasks-panel">
      <div className="text-sm text-gray-500">Total tareas pendientes: {tasks.total}</div>
      {renderTaskList(tasks.overdue, 'Vencidas', 'bg-red-500')}
      {renderTaskList(tasks.today, 'Hoy', 'bg-blue-500')}
      {renderTaskList(tasks.pending_first_contact, 'Pendientes primer contacto', 'bg-yellow-500')}
      {tasks.total === 0 && (
        <Card><CardContent className="py-8 text-center text-gray-500">No tienes tareas pendientes</CardContent></Card>
      )}
    </div>
  );
}

function SurveysSummaryPanel({ campaignId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await axiosInstance.get(`/campaigns/${campaignId}/surveys-summary`);
      setSummary(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Cargando resumen...</div>;
  if (!summary) return null;

  const moduleUsage = summary.module_usage || {};

  return (
    <div className="space-y-4" data-testid="surveys-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-4 text-center">
          <p className="text-sm text-gray-500">Encuestas</p>
          <p className="text-2xl font-bold">{summary.total_surveys}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 text-center">
          <p className="text-sm text-gray-500">Nota media</p>
          <p className="text-2xl font-bold text-yellow-600">{summary.avg_nota}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 text-center">
          <p className="text-sm text-gray-500">NPS</p>
          <p className={`text-2xl font-bold ${summary.nps >= 0 ? 'text-green-600' : 'text-red-600'}`}>{summary.nps}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 text-center">
          <p className="text-sm text-gray-500">Promotores</p>
          <p className="text-2xl font-bold text-green-600">{summary.promotores}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 text-center">
          <p className="text-sm text-gray-500">Detractores</p>
          <p className="text-2xl font-bold text-red-600">{summary.detractores}</p>
        </CardContent></Card>
      </div>

      {summary.total_surveys > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Uso de Modulos</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(moduleUsage).map(([key, count]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                  <span className="font-medium">{count}/{summary.total_surveys}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const MODULE_STATUS_COLORS = {
  no_presentado: 'bg-gray-100 text-gray-500',
  presentado: 'bg-blue-100 text-blue-700',
  interesado: 'bg-purple-100 text-purple-700',
  no_interesado: 'bg-red-100 text-red-700',
  pendiente_demo: 'bg-yellow-100 text-yellow-700',
  presupuesto_enviado: 'bg-orange-100 text-orange-700',
  cerrado: 'bg-green-100 text-green-700',
};

function ModulesMatrixPanel({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState(null); // { contactId, moduleId }
  const [showModuleManager, setShowModuleManager] = useState(false);
  const [allModules, setAllModules] = useState([]);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleSubs, setNewModuleSubs] = useState('');
  const [newModuleCampaignOnly, setNewModuleCampaignOnly] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  useEffect(() => { fetchMatrix(); }, []);

  const fetchMatrix = async () => {
    try {
      const res = await axiosInstance.get(`/campaigns/${campaignId}/modules-matrix`);
      setData(res.data);
    } catch (e) { console.error(e); toast.error('Error al cargar modulos'); }
    finally { setLoading(false); }
  };

  const fetchAllModules = async () => {
    try {
      const res = await axiosInstance.get(`/campaigns/modules/list?campaign_id=${campaignId}`);
      setAllModules(res.data);
    } catch (e) { console.error(e); }
  };

  const handleUpdateModule = async (contactId, moduleId, updateData) => {
    try {
      await axiosInstance.put(`/campaigns/${campaignId}/contacts/${contactId}/modules/${moduleId}`, updateData);
      setData(prev => {
        const newMatrix = { ...prev.matrix };
        if (!newMatrix[contactId]) newMatrix[contactId] = {};
        newMatrix[contactId][moduleId] = { ...newMatrix[contactId][moduleId], ...updateData };
        return { ...prev, matrix: newMatrix };
      });
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const handleUpdateSubmodule = async (contactId, moduleId, submoduleId, updateData) => {
    try {
      await axiosInstance.put(`/campaigns/${campaignId}/contacts/${contactId}/modules/${moduleId}/submodule/${submoduleId}`, updateData);
      // Update local state
      setData(prev => {
        const newMatrix = { ...prev.matrix };
        const cm = newMatrix[contactId]?.[moduleId] || {};
        const subs = [...(cm.submodulos_status || [])];
        const idx = subs.findIndex(s => s.submodule_id === submoduleId);
        if (idx >= 0) {
          subs[idx] = { ...subs[idx], ...updateData };
        } else {
          subs.push({ submodule_id: submoduleId, ...updateData });
        }
        if (!newMatrix[contactId]) newMatrix[contactId] = {};
        newMatrix[contactId][moduleId] = { ...cm, submodulos_status: subs };
        return { ...prev, matrix: newMatrix };
      });
    } catch (e) { toast.error('Error al actualizar sub-modulo'); }
  };

  const handleAddModule = async () => {
    if (!newModuleName.trim()) return;
    try {
      const submodulos = newModuleSubs.split(',').map(s => s.trim()).filter(Boolean);
      await axiosInstance.post('/campaigns/modules', {
        nombre_modulo: newModuleName.trim(),
        submodulos: submodulos,
        campaign_id: newModuleCampaignOnly ? campaignId : null
      });
      toast.success('Modulo creado');
      setNewModuleName('');
      setNewModuleSubs('');
      setNewModuleCampaignOnly(false);
      fetchAllModules();
      fetchMatrix();
    } catch (e) { toast.error('Error al crear modulo'); }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!(await confirmAsync('Eliminar este modulo y todos sus datos asociados?'))) return;
    try {
      await axiosInstance.delete(`/campaigns/modules/${moduleId}`);
      toast.success('Modulo eliminado');
      fetchAllModules();
      fetchMatrix();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleUpdateModuleDef = async (moduleId, updateData) => {
    try {
      await axiosInstance.put(`/campaigns/modules/${moduleId}`, updateData);
      toast.success('Modulo actualizado');
      fetchAllModules();
      fetchMatrix();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Cargando matriz de modulos...</div>;
  if (!data || data.contacts.length === 0) return (
    <div className="space-y-4">
      <Button variant="outline" onClick={() => { fetchAllModules(); setShowModuleManager(true); }} data-testid="manage-modules-btn">
        <Package className="w-4 h-4 mr-2" /> Gestionar Modulos
      </Button>
      <Card><CardContent className="py-12 text-center">
        <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium">No hay contactos en la campana</h3>
        <p className="text-gray-500">Anade contactos primero desde la pestana Contactos</p>
      </CardContent></Card>
      {showModuleManager && <ModuleManagerDialog modules={allModules} onClose={() => setShowModuleManager(false)} onAddModule={handleAddModule} onDeleteModule={handleDeleteModule} onUpdateModule={handleUpdateModuleDef} newModuleName={newModuleName} setNewModuleName={setNewModuleName} newModuleSubs={newModuleSubs} setNewModuleSubs={setNewModuleSubs} newModuleCampaignOnly={newModuleCampaignOnly} setNewModuleCampaignOnly={setNewModuleCampaignOnly} campaignId={campaignId} editingModule={editingModule} setEditingModule={setEditingModule} />}
    </div>
  );

  const { contacts, modules, matrix } = data;

  return (
    <div className="space-y-4" data-testid="modules-matrix-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{contacts.length} contactos x {modules.length} modulos. Haz clic en un modulo para ver sub-modulos.</p>
        <Button variant="outline" size="sm" onClick={() => { fetchAllModules(); setShowModuleManager(true); }} data-testid="manage-modules-btn">
          <Package className="w-4 h-4 mr-2" /> Gestionar Modulos
        </Button>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="sticky left-0 bg-gray-50 z-10 text-left p-2 min-w-[180px] font-medium text-gray-700">Cliente</th>
              <th className="text-left p-2 min-w-[80px] font-medium text-gray-500">Estado</th>
              {modules.map(mod => (
                <th key={mod.id} className="text-center p-2 min-w-[100px] font-medium text-gray-700" title={mod.nombre_modulo}>
                  <div className="truncate max-w-[100px]">{mod.nombre_modulo}</div>
                  {mod.submodulos?.length > 0 && (
                    <div className="text-[9px] text-gray-400 font-normal">{mod.submodulos.length} sub</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contacts.map(contact => {
              const contactModules = matrix[contact.id] || {};
              const statusInfo = getStatusInfo(contact.estado);
              return (
                <React.Fragment key={contact.id}>
                  <tr className="border-b hover:bg-gray-50" data-testid={`matrix-row-${contact.id}`}>
                    <td className="sticky left-0 bg-white z-10 p-2 border-r">
                      <p className="font-medium text-gray-900 truncate max-w-[170px]">{contact.cliente_nombre}</p>
                      <p className="text-[10px] text-gray-400 truncate">{contact.comercial_nombre || 'Sin asignar'}</p>
                    </td>
                    <td className="p-2">
                      <Badge className={`${statusInfo.color} text-white text-[10px] px-1`}>{statusInfo.label}</Badge>
                    </td>
                    {modules.map(mod => {
                      const cm = contactModules[mod.id] || { estado_modulo: 'no_presentado', submodulos_status: [] };
                      const colorClass = MODULE_STATUS_COLORS[cm.estado_modulo] || MODULE_STATUS_COLORS.no_presentado;
                      const isExpanded = expandedModule?.contactId === contact.id && expandedModule?.moduleId === mod.id;
                      const hasSubs = mod.submodulos?.length > 0;

                      // Count sub-module progress
                      const subsStatus = cm.submodulos_status || [];
                      const subsInterested = subsStatus.filter(s => s.estado === 'interesado' || s.estado === 'cerrado').length;

                      return (
                        <td key={mod.id} className="p-1 text-center">
                          <button
                            onClick={() => {
                              if (hasSubs) {
                                setExpandedModule(isExpanded ? null : { contactId: contact.id, moduleId: mod.id });
                              } else {
                                // Cycle through statuses for modules without subs
                                const statuses = ['no_presentado', 'presentado', 'interesado', 'no_interesado', 'pendiente_demo', 'presupuesto_enviado', 'cerrado'];
                                const currentIdx = statuses.indexOf(cm.estado_modulo);
                                const nextStatus = statuses[(currentIdx + 1) % statuses.length];
                                handleUpdateModule(contact.id, mod.id, { estado_modulo: nextStatus });
                              }
                            }}
                            className={`px-2 py-1 rounded text-[10px] font-medium cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all ${colorClass} ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                            title={hasSubs ? 'Clic para ver sub-modulos' : 'Clic para cambiar estado'}
                            data-testid={`matrix-cell-${contact.id}-${mod.id}`}
                          >
                            {MODULE_STATUSES.find(s => s.value === cm.estado_modulo)?.label?.substring(0, 8) || '-'}
                            {hasSubs && subsInterested > 0 && <span className="ml-1 text-green-600">({subsInterested})</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Sub-modules detail row */}
                  {expandedModule?.contactId === contact.id && modules.map(mod => {
                    if (expandedModule?.moduleId !== mod.id || !mod.submodulos?.length) return null;
                    const cm = contactModules[mod.id] || { submodulos_status: [] };
                    const subsStatus = cm.submodulos_status || [];

                    return (
                      <tr key={`sub-${contact.id}-${mod.id}`} className="bg-blue-50 border-b">
                        <td colSpan={modules.length + 2} className="p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm text-blue-800">{mod.nombre_modulo} - Sub-modulos para {contact.cliente_nombre}</span>
                            <Select value={cm.estado_modulo || 'no_presentado'} onValueChange={(v) => handleUpdateModule(contact.id, mod.id, { estado_modulo: v })}>
                              <SelectTrigger className="h-7 text-xs w-[160px] ml-auto"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {MODULE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {mod.submodulos.filter(s => s.activo !== false).map(sub => {
                              const subStatus = subsStatus.find(ss => ss.submodule_id === sub.id) || { estado: 'no_presentado', interes: null };
                              const subColor = MODULE_STATUS_COLORS[subStatus.estado] || MODULE_STATUS_COLORS.no_presentado;

                              return (
                                <div key={sub.id} className="flex items-center gap-2 p-2 bg-white rounded border" data-testid={`submodule-${contact.id}-${sub.id}`}>
                                  <span className="text-xs font-medium flex-1">{sub.nombre}</span>
                                  <Select value={subStatus.estado || 'no_presentado'} onValueChange={(v) => handleUpdateSubmodule(contact.id, mod.id, sub.id, { nombre: sub.nombre, estado: v })}>
                                    <SelectTrigger className={`h-6 text-[10px] w-[110px] ${subColor} border-0`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {MODULE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {Object.entries(MODULE_STATUS_COLORS).map(([key, cls]) => (
          <span key={key} className={`px-2 py-0.5 rounded ${cls}`}>
            {MODULE_STATUSES.find(s => s.value === key)?.label || key}
          </span>
        ))}
      </div>

      {showModuleManager && <ModuleManagerDialog modules={allModules} onClose={() => setShowModuleManager(false)} onAddModule={handleAddModule} onDeleteModule={handleDeleteModule} onUpdateModule={handleUpdateModuleDef} newModuleName={newModuleName} setNewModuleName={setNewModuleName} newModuleSubs={newModuleSubs} setNewModuleSubs={setNewModuleSubs} newModuleCampaignOnly={newModuleCampaignOnly} setNewModuleCampaignOnly={setNewModuleCampaignOnly} campaignId={campaignId} editingModule={editingModule} setEditingModule={setEditingModule} />}
    </div>
  );
}

function ModuleManagerDialog({ modules, onClose, onAddModule, onDeleteModule, onUpdateModule, newModuleName, setNewModuleName, newModuleSubs, setNewModuleSubs, newModuleCampaignOnly, setNewModuleCampaignOnly, campaignId, editingModule, setEditingModule }) {
  const [editName, setEditName] = useState('');
  const [editSubs, setEditSubs] = useState('');

  const startEdit = (mod) => {
    setEditingModule(mod.id);
    setEditName(mod.nombre_modulo);
    setEditSubs((mod.submodulos || []).map(s => s.nombre).join(', '));
  };

  const saveEdit = (mod) => {
    const submodulos = editSubs.split(',').map(s => s.trim()).filter(Boolean).map((name, i) => {
      const existing = (mod.submodulos || []).find(s => s.nombre === name);
      return existing || { nombre: name };
    });
    onUpdateModule(mod.id, { nombre_modulo: editName, submodulos });
    setEditingModule(null);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gestionar Modulos</DialogTitle></DialogHeader>

        {/* Add new module */}
        <Card className="border-dashed">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-medium text-sm">Anadir nuevo modulo</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nombre del modulo</Label>
                <Input value={newModuleName} onChange={e => setNewModuleName(e.target.value)} placeholder="Ej: Reservas Online" data-testid="new-module-name" />
              </div>
              <div>
                <Label className="text-xs">Sub-modulos (separados por coma)</Label>
                <Input value={newModuleSubs} onChange={e => setNewModuleSubs(e.target.value)} placeholder="Ej: Plan Basic, Plan Pro" data-testid="new-module-subs" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={newModuleCampaignOnly} onChange={e => setNewModuleCampaignOnly(e.target.checked)} className="rounded" />
                Solo para esta campana
              </label>
              <Button size="sm" onClick={onAddModule} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid="add-module-btn">
                <Plus className="w-4 h-4 mr-1" /> Anadir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Module list */}
        <div className="space-y-2">
          {modules.map(mod => (
            <Card key={mod.id} className={mod.campaign_id ? 'border-blue-200 bg-blue-50/30' : ''}>
              <CardContent className="p-3">
                {editingModule === mod.id ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-8 text-sm" />
                      <Input value={editSubs} onChange={e => setEditSubs(e.target.value)} placeholder="Sub-modulos separados por coma" className="h-8 text-sm" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditingModule(null)}>Cancelar</Button>
                      <Button size="sm" onClick={() => saveEdit(mod)} className="bg-[#9C0F17] hover:bg-[#7A0C12]">Guardar</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{mod.nombre_modulo}</span>
                        {mod.campaign_id && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Solo campana</Badge>}
                        {mod.categoria && <span className="text-[10px] text-gray-400">{mod.categoria}</span>}
                      </div>
                      {mod.submodulos?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {mod.submodulos.filter(s => s.activo !== false).map(sub => (
                            <span key={sub.id} className="px-2 py-0.5 bg-gray-100 rounded text-[10px] text-gray-600">{sub.nombre}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(mod)} data-testid={`edit-module-${mod.id}`}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDeleteModule(mod.id)} data-testid={`delete-module-${mod.id}`}>
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <DialogFooter><Button onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendFilterPanel({ campaignId, campaign }) {
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegalFooter, setShowLegalFooter] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);

  useEffect(() => { fetchFilter(); }, []);

  const fetchFilter = async () => {
    try {
      const res = await axiosInstance.get(`/marketing/campaign-filter/${campaignId}`);
      setFilter(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSendCampaign = async (testMode = false) => {
    setSending(true);
    setSendResult(null);
    setConfirmSend(false);
    try {
      const res = await axiosInstance.post(`/marketing/send-campaign/${campaignId}`, { test_mode: testMode });
      setSendResult(res.data);
      toast.success(res.data.message);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} emails fallidos`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al enviar campaña';
      toast.error(msg);
      setSendResult({ error: msg });
    } finally {
      setSending(false);
    }
  };

  const exportExcluded = () => {
    if (!filter) return;
    const all = [
      ...filter.excluded_opt_out.map(e => ({...e, motivo: 'Opt-out marketing'})),
      ...filter.excluded_inactive.map(e => ({...e, motivo: 'Inactivo'})),
      ...filter.excluded_cancelled.map(e => ({...e, motivo: 'Cancelado/Baja pendiente'})),
      ...filter.excluded_invalid_email.map(e => ({...e, motivo: 'Email invalido'})),
    ];
    const csv = 'Nombre,Email,Motivo\n' + all.map(e => `"${e.name}","${e.email}","${e.motivo}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `excluidos_${campaign?.nombre || 'campana'}.csv`; a.click();
  };

  if (loading) return <div className="text-center py-4 text-gray-400">Analizando contactos...</div>;
  if (!filter) return null;

  const { summary } = filter;

  return (
    <div className="space-y-4" data-testid="send-filter-panel">
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
            <div><p className="text-xs text-gray-500">Total contactos</p><p className="text-2xl font-bold">{filter.total}</p></div>
            <div><p className="text-xs text-gray-500">Enviables</p><p className="text-2xl font-bold text-green-600">{filter.sendable}</p></div>
            <div><p className="text-xs text-gray-500">Opt-out</p><p className="text-2xl font-bold text-red-600">{summary.opt_out}</p></div>
            <div><p className="text-xs text-gray-500">Inactivos</p><p className="text-2xl font-bold text-orange-600">{summary.inactive}</p></div>
            <div><p className="text-xs text-gray-500">Cancelados</p><p className="text-2xl font-bold text-gray-600">{summary.cancelled}</p></div>
            <div><p className="text-xs text-gray-500">Email invalido</p><p className="text-2xl font-bold text-yellow-600">{summary.invalid_email}</p></div>
          </div>
        </CardContent>
      </Card>

      {summary.total_excluded > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm text-gray-700">Contactos excluidos ({summary.total_excluded})</h4>
              <Button variant="outline" size="sm" onClick={exportExcluded} data-testid="export-excluded-btn">
                Exportar CSV
              </Button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
              {filter.excluded_opt_out.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-1.5 bg-red-50 rounded">
                  <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px]">Opt-out</span>
                  <span className="font-medium">{e.name}</span>
                  <span className="text-gray-400">{e.email}</span>
                </div>
              ))}
              {filter.excluded_cancelled.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded">
                  <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-[10px]">Cancelado</span>
                  <span className="font-medium">{e.name}</span>
                  <span className="text-gray-400">{e.email}</span>
                </div>
              ))}
              {filter.excluded_inactive.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-1.5 bg-orange-50 rounded">
                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px]">Inactivo</span>
                  <span className="font-medium">{e.name}</span>
                  <span className="text-gray-400">{e.email}</span>
                </div>
              ))}
              {filter.excluded_invalid_email.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-1.5 bg-yellow-50 rounded">
                  <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px]">Email</span>
                  <span className="font-medium">{e.name}</span>
                  <span className="text-gray-400">{e.email}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legal footer preview */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-sm text-gray-700">Footer legal (obligatorio en emails)</h4>
            <Button variant="ghost" size="sm" onClick={() => setShowLegalFooter(!showLegalFooter)}>
              {showLegalFooter ? 'Ocultar' : 'Vista previa'}
            </Button>
          </div>
          {showLegalFooter && (
            <div className="p-4 bg-gray-50 border rounded text-xs text-gray-500 leading-relaxed">
              <p>Has recibido esta comunicacion por ser o haber sido cliente de Camarero 10 y estar relacionada con la mejora de la calidad del servicio y funcionalidades asociadas.</p>
              <p className="mt-2">Puedes oponerte en cualquier momento a seguir recibiendo comunicaciones comerciales haciendo clic en <span className="text-red-600 underline">Darme de baja</span>.</p>
              <p className="mt-2">Tambien puedes ejercer tus derechos de proteccion de datos (acceso, rectificacion, supresion, oposicion, limitacion y portabilidad) escribiendo a <span className="text-red-600">soporte.clientes@camarero10.com</span>.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send campaign actions */}
      <Card className="border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-sm text-gray-700 mb-3">Enviar emails de campaña</h4>
          {!campaign?.plantilla_email_asunto || !campaign?.plantilla_email_cuerpo ? (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
              <p className="font-medium">Plantilla de email no configurada</p>
              <p className="text-xs mt-1">Configura el asunto y cuerpo del email en la pestaña de plantilla antes de enviar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <p><strong>Asunto:</strong> {campaign.plantilla_email_asunto}</p>
                <p className="mt-1 line-clamp-2"><strong>Cuerpo:</strong> {campaign.plantilla_email_cuerpo?.replace(/<[^>]*>/g, '').substring(0, 150)}...</p>
                <p className="mt-1 text-blue-500">El footer legal RGPD se añade automaticamente a cada email.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendCampaign(true)}
                  disabled={sending || filter.sendable === 0}
                  data-testid="send-test-email-btn"
                >
                  {sending ? 'Enviando...' : 'Enviar email de prueba (1 contacto)'}
                </Button>
                {!confirmSend ? (
                  <Button
                    className="bg-[#9C0F17] hover:bg-[#7a0c12]"
                    size="sm"
                    onClick={() => setConfirmSend(true)}
                    disabled={sending || filter.sendable === 0}
                    data-testid="send-campaign-btn"
                  >
                    Enviar a {filter.sendable} contactos
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-medium">¿Confirmar envio a {filter.sendable} contactos?</span>
                    <Button
                      className="bg-[#9C0F17] hover:bg-[#7a0c12]"
                      size="sm"
                      onClick={() => handleSendCampaign(false)}
                      disabled={sending}
                      data-testid="confirm-send-btn"
                    >
                      {sending ? 'Enviando...' : 'Si, enviar'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmSend(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {sendResult && !sendResult.error && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
              <p className="font-medium">{sendResult.message}</p>
              {sendResult.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  {sendResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
          {sendResult?.error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {sendResult.error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ====== MAIN COMPONENT ======

export default function CampaignDetail({ user }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contacts');
  
  const [addContactsDialogOpen, setAddContactsDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactModules, setContactModules] = useState([]);
  
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [selectedComercial, setSelectedComercial] = useState('');
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [comercialFilter, setComercialFilter] = useState('all');
  const [expandedContact, setExpandedContact] = useState(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ plantilla_email_asunto: '', plantilla_email_cuerpo: '', plantilla_whatsapp: '' });
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const excelInputRef = useRef(null);

  useEffect(() => {
    fetchCampaignData();
    fetchComerciales();
  }, [campaignId]);

  const fetchCampaignData = async () => {
    try {
      const [campaignRes, contactsRes, dashboardRes] = await Promise.all([
        axiosInstance.get(`/campaigns/${campaignId}`),
        axiosInstance.get(`/campaigns/${campaignId}/contacts`),
        axiosInstance.get(`/campaigns/${campaignId}/dashboard`)
      ]);
      setCampaign(campaignRes.data);
      setContacts(contactsRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Error al cargar la campana');
    } finally {
      setLoading(false);
    }
  };

  const fetchComerciales = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setComerciales(response.data.filter(u => u.is_active));
    } catch (error) { console.error(error); }
  };

  const searchCustomers = async () => {
    try {
      const response = await axiosInstance.get('/campaigns/customers/search');
      const existingIds = contacts.map(c => c.cliente_id);
      setAvailableCustomers(response.data.filter(c => !existingIds.includes(c.id)));
    } catch (error) { toast.error('Error al buscar clientes'); }
  };

  const handleAddContacts = async () => {
    if (selectedCustomers.length === 0) { toast.error('Selecciona al menos un cliente'); return; }
    try {
      const response = await axiosInstance.post(`/campaigns/${campaignId}/contacts/bulk`, {
        cliente_ids: selectedCustomers,
        comercial_asignado: selectedComercial || null
      });
      toast.success(`${response.data.added} clientes anadidos`);
      if (response.data.skipped > 0) toast.info(`${response.data.skipped} ya estaban`);
      setAddContactsDialogOpen(false);
      setSelectedCustomers([]);
      fetchCampaignData();
    } catch (error) { toast.error('Error al anadir clientes'); }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(`/campaigns/${campaignId}/contacts/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.message || `Importados: ${response.data.added} contactos`}${response.data.created ? ` (${response.data.created} nuevos)` : ''}`);
      if (response.data.errors?.length > 0) {
        toast.warning(`Errores: ${response.data.errors.join(', ')}`);
      }
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar Excel');
    } finally {
      setUploadingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = '';
    }
  };

  const handleSaveTemplates = async () => {
    try {
      await axiosInstance.put(`/campaigns/${campaignId}`, {
        plantilla_email_asunto: templateForm.plantilla_email_asunto,
        plantilla_email_cuerpo: templateForm.plantilla_email_cuerpo,
        plantilla_whatsapp: templateForm.plantilla_whatsapp
      });
      toast.success('Plantillas guardadas');
      setTemplatesDialogOpen(false);
      fetchCampaignData();
    } catch (error) { toast.error('Error al guardar plantillas'); }
  };

  const openTemplatesDialog = () => {
    setTemplateForm({
      plantilla_email_asunto: campaign?.plantilla_email_asunto || '',
      plantilla_email_cuerpo: campaign?.plantilla_email_cuerpo || '',
      plantilla_whatsapp: campaign?.plantilla_whatsapp || ''
    });
    setTemplatesDialogOpen(true);
  };

  const handleUpdateContact = async (contactId, updateData) => {
    try {
      await axiosInstance.put(`/campaigns/${campaignId}/contacts/${contactId}`, updateData);
      toast.success('Contacto actualizado');
      fetchCampaignData();
      setEditContactDialogOpen(false);
    } catch (error) { toast.error('Error al actualizar contacto'); }
  };

  const handleRemoveContact = async (contactId) => {
    if (!(await confirmAsync('Eliminar este contacto de la campana?'))) return;
    try {
      await axiosInstance.delete(`/campaigns/${campaignId}/contacts/${contactId}`);
      toast.success('Contacto eliminado');
      fetchCampaignData();
    } catch (error) { toast.error('Error al eliminar contacto'); }
  };

  const fetchContactModules = async (contactId) => {
    try {
      // Fetch both the contact's module records AND the full module definitions
      const [modsRes, defsRes] = await Promise.all([
        axiosInstance.get(`/campaigns/${campaignId}/contacts/${contactId}/modules`),
        axiosInstance.get(`/campaigns/modules/list?campaign_id=${campaignId}`)
      ]);
      const existingMods = modsRes.data;
      const allModuleDefs = defsRes.data;

      // Merge: for each module definition, find existing record or create a placeholder
      const merged = allModuleDefs.map(def => {
        const existing = existingMods.find(m => m.module_id === def.id);
        return {
          module_id: def.id,
          module_nombre: def.nombre_modulo,
          estado_modulo: existing?.estado_modulo || 'no_presentado',
          interes: existing?.interes || null,
          submodulos: def.submodulos || [],
          submodulos_status: existing?.submodulos_status || [],
        };
      });
      setContactModules(merged);
    } catch (error) { toast.error('Error al cargar modulos'); }
  };

  const handleUpdateModule = async (moduleId, updateData) => {
    try {
      await axiosInstance.put(`/campaigns/${campaignId}/contacts/${selectedContact.id}/modules/${moduleId}`, updateData);
      fetchContactModules(selectedContact.id);
    } catch (error) { toast.error('Error al actualizar modulo'); }
  };

  const openModulesDialog = async (contact) => {
    setSelectedContact(contact);
    await fetchContactModules(contact.id);
    setModulesDialogOpen(true);
  };

  const filteredContacts = contacts.filter(contact => {
    if (statusFilter !== 'all' && contact.estado !== statusFilter) return false;
    if (comercialFilter !== 'all' && contact.comercial_asignado !== comercialFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!campaign) {
    return <Layout user={user}><div className="text-center py-12">Campana no encontrada</div></Layout>;
  }

  const stats = dashboard?.stats || {};
  const moduleStats = dashboard?.module_stats || [];
  const comercialStats = dashboard?.comercial_stats || [];

  return (
    <Layout user={user}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/campaigns')} data-testid="back-to-campaigns">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="campaign-title">{campaign.nombre}</h1>
          <p className="text-gray-500">{campaign.descripcion}</p>
        </div>
        <Badge className={`${campaign.activa ? 'bg-green-500' : 'bg-gray-400'} text-white`} data-testid="campaign-status-badge">
          {campaign.activa ? 'Activa' : 'Inactiva'}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-bold" data-testid="stat-total">{stats.total || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Contactados</p><p className="text-2xl font-bold text-blue-600">{stats.contactados || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Interesados</p><p className="text-2xl font-bold text-purple-600">{stats.interesados || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Estudios</p><p className="text-2xl font-bold text-indigo-600">{stats.estudios_enviados || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Ganados</p><p className="text-2xl font-bold text-green-600">{stats.cerrados_ganados || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-sm text-gray-500">Conversion</p><p className="text-2xl font-bold text-orange-600">{stats.conversion_rate || 0}%</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="w-4 h-4 mr-2" /> Contactos ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">
            <Package className="w-4 h-4 mr-2" /> Modulos
          </TabsTrigger>
          <TabsTrigger value="surveys" data-testid="tab-surveys">
            <Star className="w-4 h-4 mr-2" /> Encuestas
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertTriangle className="w-4 h-4 mr-2" /> Alertas
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            <ClipboardList className="w-4 h-4 mr-2" /> Mis Tareas
          </TabsTrigger>
          <TabsTrigger value="send" data-testid="tab-send">
            <Send className="w-4 h-4 mr-2" /> Preparar Envio
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
          </TabsTrigger>
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { searchCustomers(); setAddContactsDialogOpen(true); }} className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white" data-testid="add-contacts-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Anadir Clientes
              </Button>
              <Button variant="outline" onClick={() => excelInputRef.current?.click()} disabled={uploadingExcel} data-testid="import-excel-btn">
                <Upload className="w-4 h-4 mr-2" /> {uploadingExcel ? 'Importando...' : 'Importar Excel'}
              </Button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/template/excel-download`}
                onClick={(e) => {
                  e.preventDefault();
                  axiosInstance.get('/campaigns/template/excel-download', { responseType: 'blob' }).then(res => {
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = 'plantilla_campana.xlsx'; a.click();
                  }).catch(() => toast.error('Error al descargar plantilla'));
                }}
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                data-testid="download-template-link"
              >
                Descargar plantilla
              </a>
              <input ref={excelInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelImport} />
              <Button variant="outline" onClick={openTemplatesDialog} data-testid="templates-btn">
                <FileText className="w-4 h-4 mr-2" /> Plantillas
              </Button>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue placeholder="Filtrar por estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {CONTACT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={comercialFilter} onValueChange={setComercialFilter}>
                <SelectTrigger className="w-[180px]" data-testid="comercial-filter"><SelectValue placeholder="Filtrar por comercial" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los comerciales</SelectItem>
                  {comerciales.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            {filteredContacts.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No hay contactos</h3>
                <p className="text-gray-500">Anade clientes a esta campana para empezar</p>
              </CardContent></Card>
            ) : (
              filteredContacts.map(contact => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  campaignId={campaignId}
                  campaign={campaign}
                  expandedContact={expandedContact}
                  setExpandedContact={setExpandedContact}
                  onEdit={(c) => { setSelectedContact(c); setEditContactDialogOpen(true); }}
                  onModules={openModulesDialog}
                  onRefresh={fetchCampaignData}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Modules Tab - Phase 3 */}
        <TabsContent value="modules">
          <ModulesMatrixPanel campaignId={campaignId} />
        </TabsContent>

        {/* Surveys Tab */}
        <TabsContent value="surveys">
          <SurveysSummaryPanel campaignId={campaignId} />
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <AlertsPanel campaignId={campaignId} />
        </TabsContent>

        {/* My Tasks Tab */}
        <TabsContent value="tasks">
          <MyTasksPanel />
        </TabsContent>

        {/* Send Filter Tab */}
        <TabsContent value="send">
          <SendFilterPanel campaignId={campaignId} campaign={campaign} />
        </TabsContent>

        {/* Dashboard Tab - Phase 4 */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Contactados</p>
              <p className="text-2xl font-bold text-blue-600">{stats.contactados || 0}<span className="text-sm font-normal text-gray-400">/{stats.total || 0}</span></p>
              <p className="text-xs text-gray-400">{stats.total ? Math.round((stats.contactados / stats.total) * 100) : 0}% alcance</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-green-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Conversion</p>
              <p className="text-2xl font-bold text-green-600">{stats.conversion_rate || 0}%</p>
              <p className="text-xs text-gray-400">{stats.cerrados_ganados || 0} ganados</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-purple-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Volumen cerrado</p>
              <p className="text-2xl font-bold text-purple-600">{(stats.volumen_total || 0).toLocaleString('es-ES')} <span className="text-sm">EUR</span></p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-orange-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Pipeline</p>
              <p className="text-2xl font-bold text-orange-600">{(dashboard?.pipeline_value || 0).toLocaleString('es-ES')} <span className="text-sm">EUR</span></p>
              <p className="text-xs text-gray-400">ponderado por probabilidad</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-yellow-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">NPS</p>
              <p className={`text-2xl font-bold ${(dashboard?.satisfaction?.nps || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{dashboard?.satisfaction?.nps || 0}</p>
              <p className="text-xs text-gray-400">{dashboard?.satisfaction?.total_surveys || 0} encuestas</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-red-500"><CardContent className="pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Alertas</p>
              <p className="text-2xl font-bold text-red-600">{dashboard?.unread_alerts || 0}</p>
              <p className="text-xs text-gray-400">sin leer</p>
            </CardContent></Card>
          </div>

          {/* Funnel */}
          <Card>
            <CardHeader><CardTitle className="text-base">Embudo de Conversion</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(dashboard?.funnel || []).map((step, idx) => {
                  const maxCount = dashboard?.funnel?.[0]?.count || 1;
                  const pct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-36 text-sm font-medium text-gray-700">{step.etapa}</div>
                      <div className="flex-1 relative">
                        <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                          <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: step.color }}></div>
                        </div>
                      </div>
                      <div className="w-16 text-right">
                        <span className="text-lg font-bold">{step.count}</span>
                        <span className="text-xs text-gray-400 ml-1">{Math.round(pct)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Breakdown */}
            <Card>
              <CardHeader><CardTitle className="text-base">Distribucion por Estado</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(stats.status_breakdown || {}).map(([status, count]) => {
                    const statusInfo = getStatusInfo(status);
                    const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                    return (
                      <div key={status} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className={`w-3 h-3 rounded-full ${statusInfo.color}`}></div>
                        <span className="text-xs flex-1">{statusInfo.label}</span>
                        <span className="text-sm font-bold">{count}</span>
                        <span className="text-xs text-gray-400">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Interaction Breakdown */}
            <Card>
              <CardHeader><CardTitle className="text-base">Interacciones por Tipo</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(dashboard?.interaction_breakdown || {}).length === 0 ? (
                  <p className="text-center text-gray-400 py-4">Sin interacciones registradas</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(dashboard?.interaction_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => {
                      const totalInteractions = Object.values(dashboard?.interaction_breakdown || {}).reduce((a, b) => a + b, 0);
                      const pct = totalInteractions > 0 ? (count / totalInteractions) * 100 : 0;
                      const colors = { email: '#3B82F6', whatsapp: '#22C55E', llamada: '#F59E0B', reunion: '#8B5CF6', demo: '#EC4899', visita: '#06B6D4' };
                      return (
                        <div key={tipo} className="flex items-center gap-3">
                          <span className="w-20 text-sm capitalize">{tipo}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-5">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: colors[tipo] || '#6B7280' }}></div>
                          </div>
                          <span className="text-sm font-bold w-10 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NPS & Satisfaction */}
            <Card>
              <CardHeader><CardTitle className="text-base">Satisfaccion (NPS)</CardTitle></CardHeader>
              <CardContent>
                {(dashboard?.satisfaction?.total_surveys || 0) === 0 ? (
                  <p className="text-center text-gray-400 py-4">Sin encuestas registradas</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-8">
                      <div className="text-center">
                        <p className={`text-4xl font-bold ${(dashboard?.satisfaction?.nps || 0) >= 50 ? 'text-green-600' : (dashboard?.satisfaction?.nps || 0) >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {dashboard?.satisfaction?.nps || 0}
                        </p>
                        <p className="text-xs text-gray-500">NPS Score</p>
                      </div>
                      <div className="text-center">
                        <p className="text-4xl font-bold text-yellow-500">{dashboard?.satisfaction?.avg_nota || 0}</p>
                        <p className="text-xs text-gray-500">Nota media /10</p>
                      </div>
                    </div>
                    <div className="flex justify-center gap-6">
                      <div className="text-center"><p className="text-lg font-bold text-green-600">{dashboard?.satisfaction?.promotores || 0}</p><p className="text-xs text-gray-500">Promotores</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-gray-500">{(dashboard?.satisfaction?.total_surveys || 0) - (dashboard?.satisfaction?.promotores || 0) - (dashboard?.satisfaction?.detractores || 0)}</p><p className="text-xs text-gray-500">Neutros</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-red-600">{dashboard?.satisfaction?.detractores || 0}</p><p className="text-xs text-gray-500">Detractores</p></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader><CardTitle className="text-base">Actividad Reciente</CardTitle></CardHeader>
              <CardContent>
                {(dashboard?.recent_interactions || []).length === 0 ? (
                  <p className="text-center text-gray-400 py-4">Sin actividad reciente</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(dashboard?.recent_interactions || []).slice(0, 10).map((inter, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-gray-50 rounded">
                        <span className={`px-1.5 py-0.5 rounded text-white capitalize ${inter.tipo === 'email' ? 'bg-blue-500' : inter.tipo === 'whatsapp' ? 'bg-green-500' : inter.tipo === 'llamada' ? 'bg-yellow-500' : 'bg-purple-500'}`}>
                          {inter.tipo}
                        </span>
                        <div className="flex-1">
                          <span className="font-medium">{inter.cliente_nombre}</span>
                          {inter.resultado && <span className="text-gray-500"> - {inter.resultado}</span>}
                        </div>
                        <span className="text-gray-400 shrink-0">{new Date(inter.fecha).toLocaleDateString('es-ES')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Module Interest */}
          <Card>
            <CardHeader><CardTitle className="text-base">Interes por Modulo</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {moduleStats.map(mod => {
                  const pctInterest = mod.total > 0 ? (mod.interesados / mod.total) * 100 : 0;
                  return (
                    <div key={mod.module_id} className="flex items-center gap-4">
                      <div className="w-44 font-medium text-sm truncate">{mod.module_nombre}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden flex">
                            <div className="bg-purple-500 h-full" style={{ width: `${pctInterest}%` }}></div>
                            <div className="bg-green-500 h-full" style={{ width: `${mod.total > 0 ? (mod.cerrados / mod.total) * 100 : 0}%` }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs shrink-0">
                        <span className="text-purple-600 font-medium">{mod.interesados} interes.</span>
                        <span className="text-green-600 font-medium">{mod.cerrados} cerr.</span>
                        <span className="text-gray-400">{mod.total} total</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Sub-module Interest */}
          {(dashboard?.submodule_interest || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Top Sub-modulos con Interes</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(dashboard?.submodule_interest || []).filter(s => s.interesados > 0).map((sub, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                      <div className="flex-1 text-xs font-medium truncate">{sub.nombre}</div>
                      <Badge className="bg-purple-100 text-purple-700 text-[10px]">{sub.interesados} int.</Badge>
                      {sub.cerrados > 0 && <Badge className="bg-green-100 text-green-700 text-[10px]">{sub.cerrados} cerr.</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comercial Performance */}
          <Card>
            <CardHeader><CardTitle className="text-base">Rendimiento por Comercial</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3">Comercial</th>
                    <th className="text-center py-2 px-3">Asignados</th>
                    <th className="text-center py-2 px-3">Contactados</th>
                    <th className="text-center py-2 px-3">Ganados</th>
                    <th className="text-center py-2 px-3">Conversion</th>
                    <th className="text-right py-2 px-3">Volumen</th>
                  </tr></thead>
                  <tbody>
                    {comercialStats.map(com => (
                      <tr key={com.comercial_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{com.comercial_nombre}</td>
                        <td className="text-center py-2 px-3">{com.total}</td>
                        <td className="text-center py-2 px-3">{com.contactados}</td>
                        <td className="text-center py-2 px-3 text-green-600 font-bold">{com.cerrados_ganados}</td>
                        <td className="text-center py-2 px-3">{com.total > 0 ? Math.round((com.cerrados_ganados / com.total) * 100) : 0}%</td>
                        <td className="text-right py-2 px-3 font-medium">{(com.volumen || 0).toLocaleString('es-ES')} EUR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Contacts Dialog */}
      <Dialog open={addContactsDialogOpen} onOpenChange={setAddContactsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Anadir Clientes a la Campana</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comercial asignado (opcional)</Label>
              <Select value={selectedComercial || '__none__'} onValueChange={v => setSelectedComercial(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar comercial" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {comerciales.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Seleccionar clientes ({selectedCustomers.length} seleccionados)</Label>
              <div className="border rounded-lg max-h-64 overflow-y-auto mt-2">
                {availableCustomers.length === 0 ? (
                  <p className="p-4 text-gray-500 text-center">No hay clientes disponibles</p>
                ) : (
                  availableCustomers.map(customer => (
                    <label key={customer.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedCustomers([...selectedCustomers, customer.id]);
                          else setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id));
                        }}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{customer.name || customer.company}</p>
                        <p className="text-sm text-gray-500">{customer.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">{customer.city}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddContactsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleAddContacts} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid="confirm-add-contacts">
              Anadir {selectedCustomers.length} clientes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={editContactDialogOpen} onOpenChange={setEditContactDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar Contacto: {selectedContact?.cliente_nombre}</DialogTitle></DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div>
                <Label>Estado</Label>
                <Select value={selectedContact.estado} onValueChange={(v) => setSelectedContact({...selectedContact, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Comercial asignado</Label>
                <Select value={selectedContact.comercial_asignado || '__none__'} onValueChange={(v) => setSelectedContact({...selectedContact, comercial_asignado: v === '__none__' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {comerciales.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Probabilidad cierre (%)</Label>
                  <Input type="number" min="0" max="100" value={selectedContact.probabilidad_cierre || 0} onChange={(e) => setSelectedContact({...selectedContact, probabilidad_cierre: parseInt(e.target.value)})} />
                </div>
                <div>
                  <Label>Volumen estimado (EUR)</Label>
                  <Input type="number" value={selectedContact.volumen_estimado || 0} onChange={(e) => setSelectedContact({...selectedContact, volumen_estimado: parseFloat(e.target.value)})} />
                </div>
              </div>
              <div>
                <Label>Proxima accion</Label>
                <Input type="date" value={selectedContact.proxima_accion?.split('T')[0] || ''} onChange={(e) => setSelectedContact({...selectedContact, proxima_accion: e.target.value})} />
                <Input className="mt-2" placeholder="Descripcion de la proxima accion" value={selectedContact.proxima_accion_descripcion || ''} onChange={(e) => setSelectedContact({...selectedContact, proxima_accion_descripcion: e.target.value})} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selectedContact.estudio_solicitado} onChange={(e) => setSelectedContact({...selectedContact, estudio_solicitado: e.target.checked})} />
                  Estudio solicitado
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selectedContact.estudio_enviado} onChange={(e) => setSelectedContact({...selectedContact, estudio_enviado: e.target.checked})} />
                  Estudio enviado
                </label>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={selectedContact.notas || ''} onChange={(e) => setSelectedContact({...selectedContact, notas: e.target.value})} className="min-h-[80px]" placeholder="Notas adicionales..." />
              </div>
              {selectedContact.estado === 'cerrado_perdido' && (
                <div>
                  <Label>Motivo no cierre</Label>
                  <Input value={selectedContact.motivo_no_cierre || ''} onChange={(e) => setSelectedContact({...selectedContact, motivo_no_cierre: e.target.value})} placeholder="Por que no se cerro?" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={() => { handleRemoveContact(selectedContact.id); setEditContactDialogOpen(false); }} data-testid="delete-contact-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Eliminar
            </Button>
            <Button variant="outline" onClick={() => setEditContactDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => handleUpdateContact(selectedContact.id, selectedContact)} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid="save-contact-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modules Dialog */}
      <Dialog open={modulesDialogOpen} onOpenChange={setModulesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Modulos: {selectedContact?.cliente_nombre}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {contactModules.length === 0 && <p className="text-center text-gray-400 py-4">No hay modulos disponibles</p>}
            {contactModules.map(mod => {
              const getSubStatus = (subId) => (mod.submodulos_status || []).find(s => s.submodule_id === subId) || { estado: 'no_presentado' };

              return (
                <Card key={mod.module_id} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1"><p className="font-medium text-sm">{mod.module_nombre}</p></div>
                      <Select value={mod.estado_modulo || 'no_presentado'} onValueChange={(v) => handleUpdateModule(mod.module_id, { estado_modulo: v })}>
                        <SelectTrigger className="w-[160px] h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{MODULE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={mod.interes || '__none__'} onValueChange={(v) => handleUpdateModule(mod.module_id, { interes: v === '__none__' ? null : v })}>
                        <SelectTrigger className="w-[100px] h-8 text-sm"><SelectValue placeholder="Interes" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-</SelectItem>
                          <SelectItem value="alto">Alto</SelectItem>
                          <SelectItem value="medio">Medio</SelectItem>
                          <SelectItem value="bajo">Bajo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {mod.submodulos?.length > 0 && (
                      <div className="pl-4 border-l-2 border-gray-200 space-y-1 mt-2">
                        {mod.submodulos.filter(s => s.activo !== false).map(sub => {
                          const subSt = getSubStatus(sub.id);
                          return (
                            <div key={sub.id} className="flex items-center gap-2 py-1">
                              <span className="text-xs text-gray-600 flex-1">{sub.nombre}</span>
                              <Select value={subSt.estado || 'no_presentado'} onValueChange={(v) => {
                                axiosInstance.put(`/campaigns/${campaignId}/contacts/${selectedContact.id}/modules/${mod.module_id}/submodule/${sub.id}`, { nombre: sub.nombre, estado: v })
                                  .then(() => fetchContactModules(selectedContact.id))
                                  .catch(() => toast.error('Error al actualizar'));
                              }}>
                                <SelectTrigger className="w-[140px] h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{MODULE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setModulesDialogOpen(false)} className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white" data-testid="close-modules-dialog">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Plantillas de la Campana</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-2">Usa <code className="bg-gray-100 px-1 rounded">{'{nombre}'}</code> para insertar el nombre del cliente automaticamente.</p>
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Plantilla Email</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={templateForm.plantilla_email_asunto}
                    onChange={e => setTemplateForm({...templateForm, plantilla_email_asunto: e.target.value})}
                    placeholder="Ej: Hola {nombre}, te presentamos nuestras novedades"
                    data-testid="template-email-subject"
                  />
                </div>
                <div>
                  <Label>Cuerpo del email</Label>
                  <Textarea
                    rows={5}
                    value={templateForm.plantilla_email_cuerpo}
                    onChange={e => setTemplateForm({...templateForm, plantilla_email_cuerpo: e.target.value})}
                    placeholder="Estimado/a {nombre},&#10;&#10;Nos ponemos en contacto contigo para..."
                    data-testid="template-email-body"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-green-600" /> Plantilla WhatsApp</CardTitle></CardHeader>
              <CardContent>
                <div>
                  <Label>Mensaje WhatsApp</Label>
                  <Textarea
                    rows={4}
                    value={templateForm.plantilla_whatsapp}
                    onChange={e => setTemplateForm({...templateForm, plantilla_whatsapp: e.target.value})}
                    placeholder="Hola {nombre}! Te escribimos desde OSAGEST para..."
                    data-testid="template-whatsapp-body"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplatesDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveTemplates} className="bg-[#9C0F17] hover:bg-[#7A0C12]" data-testid="save-templates-btn">
              Guardar Plantillas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
